<template>
  <transition name="slide-up">
    <div v-if="isVisible" class="global-upload-progress" :class="{ minimized: isMinimized }">
      <!-- Header -->
      <div class="upload-header">
        <div class="header-left">
          <div class="upload-icon-wrapper">
            <q-icon name="autorenew" size="14px" class="upload-icon" />
          </div>
          <div class="header-info">
            <span class="upload-title">
              {{ getHeaderTitle() }}
            </span>
            <span v-if="uploads.length > 0" class="upload-subtitle">
              {{ getHeaderSubtitle() }}
            </span>
          </div>
        </div>

        <div class="header-actions">
          <q-btn flat dense round size="sm" :icon="isMinimized ? 'keyboard_arrow_up' : 'keyboard_arrow_down'" @click="toggleMinimize" class="header-btn" />
          <q-btn flat dense round size="sm" icon="close" @click="handleClose" class="header-btn" />
        </div>
      </div>

      <!-- Content -->
      <div v-if="!isMinimized" class="upload-content">
        <!-- Upload items list -->
        <div v-if="uploads.length > 0" class="upload-list">
          <div v-for="upload in uploads" :key="upload.id" class="upload-item">
            <!-- Left: Preview thumbnail -->
            <div class="upload-thumbnail">
              <img
                v-if="upload.previewUrl && (upload.contentType === 'image' || upload.contentType === 'video')"
                :src="upload.previewUrl"
                :alt="upload.fileName"
                class="thumbnail-image"
                @error="handleImageError"
              />
              <div v-else class="thumbnail-fallback">
                <q-icon :name="getContentTypeIcon(upload)" size="20px" color="grey-6" />
              </div>
            </div>

            <!-- Middle: File info -->
            <div class="file-info">
              <div class="file-main-info">
                <q-icon :name="getContentTypeIcon(upload)" size="16px" color="grey-7" class="file-type-icon" />
                <span class="file-name">{{ truncateFileName(upload.fileName, 15) }}</span>
              </div>
            </div>

            <!-- Right: Action area -->
            <div class="upload-actions">
              <!-- Delete button for completed uploads -->
              <q-btn
                v-if="!shouldShowProgress(upload) && upload.progress >= 100 && upload.status !== 'error' && upload.canDelete && upload.realId"
                flat
                dense
                round
                size="sm"
                icon="delete_outline"
                color="negative"
                class="action-btn delete-btn"
                @click="handleDelete(upload)"
              >
                <q-tooltip>Delete</q-tooltip>
              </q-btn>

              <!-- Retry button for failed uploads -->
              <!-- <q-btn v-if="upload.status === 'error'" flat dense round size="sm" icon="refresh" color="warning" class="action-btn retry-btn" @click="handleRetry(upload)" disabled>
                <q-tooltip>Retry</q-tooltip>
              </q-btn> -->

              <!-- Progress indicator for active uploads -->
              <div v-if="shouldShowProgress(upload)" class="progress-indicator">
                <q-circular-progress
                  :value="getAnimatedProgress(upload)"
                  size="24px"
                  :thickness="0.4"
                  :color="getProgressColor(upload)"
                  track-color="grey-3"
                  class="upload-progress"
                  :class="{ 'progress-active': shouldShowProgress(upload) }"
                />
                <span class="progress-text">{{ Math.round(getAnimatedProgress(upload)) }}%</span>
              </div>

              <!-- Warning icon for completed with warnings -->
              <div v-else-if="!shouldShowProgress(upload) && upload.progress >= 100 && upload.status === 'warning'" class="warning-indicator">
                <q-icon name="warning" size="24px" color="orange">
                  <q-tooltip class="warning-tooltip" anchor="top middle" self="bottom middle" :offset="[0, 8]" :z-index="99999">
                    <div class="tooltip-content">
                      <div class="tooltip-message">{{ upload.message || 'Upload completed but occurred some issues' }}</div>
                    </div>
                  </q-tooltip>
                </q-icon>
              </div>

              <!-- Success checkmark -->
              <div v-else-if="!shouldShowProgress(upload) && upload.progress >= 100 && upload.status !== 'error' && upload.status !== 'warning'" class="success-indicator">
                <q-icon name="check_circle" size="24px" color="positive">
                  <q-tooltip class="success-tooltip" anchor="top middle" self="bottom middle" :offset="[0, 8]" :z-index="99999">
                    <div class="tooltip-content">
                      <div v-if="upload.message" class="tooltip-message">{{ upload.message }}</div>
                      <div v-else class="tooltip-message">{{ upload.fileName }} uploaded successfully</div>
                    </div>
                  </q-tooltip>
                </q-icon>
              </div>

              <!-- Error icon for failed uploads -->
              <div v-else-if="upload.status === 'error'" class="error-indicator">
                <q-icon name="error" size="24px" color="negative">
                  <q-tooltip class="error-tooltip" anchor="top middle" self="bottom middle" :offset="[0, 8]" :z-index="99999">
                    <div class="tooltip-content">
                      <div class="tooltip-message">{{ upload.message || 'An error occurred during upload' }}</div>
                    </div>
                  </q-tooltip>
                </q-icon>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { computed, ref, watch } from '@vue/composition-api';
import { useQuery } from '@tanstack/vue-query';

export default {
  name: 'GlobalUploadProgress',

  setup(props, { root }) {
    // Configuration constants
    const CONFIG = {
      DISPLAY_STEP_DURATION: 150, // ms per 1% for visible counting
      INTERNAL_STEP_DURATION: 8, // ms for internal tracking (more reasonable than 1ms)
      DISPLAY_PROGRESS_THRESHOLD: 2, // % difference before starting new display animation
      INTERNAL_PROGRESS_THRESHOLD: 0.5, // % difference before updating internal animation
      CLEANUP_TIMEOUT: 15000, // 15s cleanup timeout
      ADAPTIVE_SPEED: {
        NORMAL: 1, // 1% per step
        MODERATE: 2, // 2% per step when 10% behind
        FAST: 3 // 3% per step when 20% behind
      }
    };

    // Reactive data
    const isMinimized = ref(false);
    const animatedProgress = ref({}); // Internal animation tracking (can be fast)
    const displayProgress = ref({}); // What we show to the user (counts slowly)
    const activeAnimations = ref({}); // Track active animation intervals
    const animationTargets = ref({}); // Track current targets for each animation
    const displayIntervals = ref({}); // Track display counting intervals

    // Computed properties from Vuex store
    const isVisible = computed(() => {
      const uploads = root.$store.getters['uploadProgress/uploads'];
      return root.$store.getters['uploadProgress/isVisible'] && uploads.length > 0;
    });

    const uploads = computed(() => root.$store.getters['uploadProgress/uploads']);

    const activeUploads = computed(() => uploads.value.filter(upload => upload.progress < 100));

    const completedUploads = computed(() => uploads.value.filter(upload => upload.progress >= 100));

    const totalActiveUploads = computed(() => uploads.value.filter(upload => upload.progress < 100 && upload.status !== 'error').length);

    const hasActiveUploads = computed(() => uploads.value.some(upload => upload.progress < 100 && upload.status !== 'error'));

    const uploadsNeedingCheck = computed(() => uploads.value.filter(upload => upload.realId && upload.status !== 'error' && upload.progress < 100));

    // TanStack Query for real-time progress tracking
    const processingStatusQuery = useQuery({
      queryKey: ['upload-processing-status', computed(() => uploadsNeedingCheck.value.map(u => u.realId))],
      queryFn: async () => {
        return await root.$store.dispatch('uploadProgress/getProcessingStatus');
      },
      enabled: computed(() => uploadsNeedingCheck.value.length > 0),
      refetchInterval: 4000, // Poll every 4 seconds
      refetchIntervalInBackground: true,
      staleTime: 0, // Always consider data stale to ensure fresh polling
      refetchOnWindowFocus: false, // Don't refetch when window gains focus
      retryDelay: 1000, // Retry faster on errors
      retry: 3, // Retry up to 3 times
      onSuccess: data => {
        // Update the Vuex store with the new progress data
        const allStatusData = [...(data.images || []), ...(data.videos || [])];
        if (allStatusData.length > 0) {
          root.$store.dispatch('uploadProgress/updateUploadsFromStatus', allStatusData);
        }
      },
      onError: error => {
        console.error('Error fetching upload progress:', error);
      }
    });

    // Store actions
    const hideDialog = () => root.$store.dispatch('uploadProgress/hideDialog');
    const clearCompleted = () => root.$store.dispatch('uploadProgress/clearCompleted');
    const clearAll = () => root.$store.dispatch('uploadProgress/clearAll');

    // Methods
    const toggleMinimize = () => {
      isMinimized.value = !isMinimized.value;
    };

    const handleClose = () => {
      hideDialog();
    };

    // Optimized progress animation with better structure and performance
    const animateProgressTo = (uploadId, targetProgress) => {
      const currentAnimatedProgress = animatedProgress.value[uploadId];
      const isFirstTime = currentAnimatedProgress === undefined;

      // Early returns for optimization
      if (activeAnimations.value[uploadId]) {
        if (targetProgress > (currentAnimatedProgress || 0)) {
          animationTargets.value[uploadId] = targetProgress;
        }
        return;
      }

      const startProgress = isFirstTime ? 0 : currentAnimatedProgress || 0;
      const difference = targetProgress - startProgress;

      // Validate and handle edge cases
      if (!isFirstTime && (targetProgress <= startProgress || Math.abs(difference) < 0.1)) {
        root.$set(animatedProgress.value, uploadId, Math.max(startProgress, targetProgress));
        return;
      }

      // Initialize progress for first time
      if (isFirstTime) {
        root.$set(animatedProgress.value, uploadId, 0);
      }

      animationTargets.value[uploadId] = targetProgress;

      // Start optimized animation
      startInternalAnimation(uploadId, targetProgress);
    };

    // Separated internal animation logic for better maintainability
    const startInternalAnimation = (uploadId, targetProgress) => {
      let currentStep = 0;

      const animationInterval = setInterval(() => {
        currentStep++;
        const currentTarget = animationTargets.value[uploadId] || targetProgress;
        const currentAnimated = animatedProgress.value[uploadId] || 0;

        // Calculate adaptive step size based on gap
        const gapBehind = currentTarget - currentAnimated;
        const stepSize = getAdaptiveStepSize(gapBehind);

        // Calculate and apply new progress
        const newProgress = Math.min(currentAnimated + stepSize, currentTarget);
        root.$set(animatedProgress.value, uploadId, newProgress);

        // Check for completion
        if (newProgress >= currentTarget) {
          cleanupAnimation(uploadId, animationInterval, currentTarget);
        }
      }, CONFIG.INTERNAL_STEP_DURATION);

      // Track animation and set cleanup timeout
      activeAnimations.value[uploadId] = animationInterval;
      setTimeout(() => cleanupAnimationTimeout(uploadId, targetProgress), CONFIG.CLEANUP_TIMEOUT);
    };

    // Helper function for adaptive step sizing
    const getAdaptiveStepSize = gapBehind => {
      if (gapBehind > 20) return CONFIG.ADAPTIVE_SPEED.FAST;
      if (gapBehind > 10) return CONFIG.ADAPTIVE_SPEED.MODERATE;
      return CONFIG.ADAPTIVE_SPEED.NORMAL;
    };

    // Centralized animation cleanup
    const cleanupAnimation = (uploadId, interval, finalProgress) => {
      clearInterval(interval);
      delete activeAnimations.value[uploadId];
      delete animationTargets.value[uploadId];
      root.$set(animatedProgress.value, uploadId, finalProgress);
    };

    // Cleanup timeout handler
    const cleanupAnimationTimeout = (uploadId, targetProgress) => {
      if (activeAnimations.value[uploadId]) {
        clearInterval(activeAnimations.value[uploadId]);
        delete activeAnimations.value[uploadId];
        const finalTarget = animationTargets.value[uploadId] || targetProgress;
        root.$set(animatedProgress.value, uploadId, finalTarget);
        delete animationTargets.value[uploadId];
      }
    };

    // Optimized display counting with better performance and structure
    const startDisplayCounting = (uploadId, targetDisplay) => {
      // Clean up any existing display interval
      cleanupDisplayInterval(uploadId);

      // Initialize display progress with proper Vue reactivity
      if (!(uploadId in displayProgress.value)) {
        root.$set(displayProgress.value, uploadId, 0);
      }

      const startDisplay = displayProgress.value[uploadId] || 0;

      // Early return if already at target
      if (startDisplay >= targetDisplay) {
        root.$set(displayProgress.value, uploadId, targetDisplay);
        return;
      }

      // Start display counting animation
      const displayInterval = setInterval(() => {
        const currentDisplay = displayProgress.value[uploadId] || 0;

        if (currentDisplay >= targetDisplay) {
          cleanupDisplayInterval(uploadId);
          root.$set(displayProgress.value, uploadId, targetDisplay);
          return;
        }

        // Increment by 1% for smooth counting effect
        const newDisplay = currentDisplay + 1;
        root.$set(displayProgress.value, uploadId, Math.min(newDisplay, targetDisplay));
      }, CONFIG.DISPLAY_STEP_DURATION);

      displayIntervals.value[uploadId] = displayInterval;
    };

    // Helper function for display interval cleanup
    const cleanupDisplayInterval = uploadId => {
      if (displayIntervals.value[uploadId]) {
        clearInterval(displayIntervals.value[uploadId]);
        delete displayIntervals.value[uploadId];
      }
    };

    // Get animated progress value for display - always use display progress for counting effect
    const getAnimatedProgress = upload => {
      const display = displayProgress.value[upload.id];

      // If display progress exists, use it
      if (display !== undefined) {
        return display;
      }

      // If no display progress yet, return 0 (we'll start display animation from 0)
      return 0;
    };

    const getProgressColor = upload => {
      if (upload.status === 'error') return 'negative';
      if (upload.progress >= 100) return 'positive';

      return 'primary';
    };

    const shouldShowProgress = upload => {
      // Show progress indicator based on animated display progress, not real progress
      // This ensures the counting animation completes before showing success indicator
      const displayProgressValue = displayProgress.value[upload.id];

      // If display progress exists and hasn't reached 100%, keep showing progress
      if (displayProgressValue !== undefined && displayProgressValue < 100) {
        return upload.status !== 'error';
      }

      // If no display progress has started yet, but real progress < 100, show progress
      if (displayProgressValue === undefined && upload.progress < 100) {
        return upload.status !== 'error';
      }

      // If display progress has reached 100% or real progress is 100% with no animation, don't show progress
      return false;
    };

    const getContentTypeIcon = upload => {
      if (upload.contentType === 'video') return 'videocam';
      if (upload.contentType === 'image') return 'image';
      return 'description';
    };

    const truncateFileName = (fileName, maxLength) => {
      if (fileName.length > maxLength) {
        return fileName.slice(0, maxLength) + '...';
      }
      return fileName;
    };

    const formatFileSize = size => {
      if (size < 1024) return size + ' B';
      if (size < 1024 * 1024) return (size / 1024).toFixed(2) + ' KB';
      if (size < 1024 * 1024 * 1024) return (size / (1024 * 1024)).toFixed(2) + ' MB';
      return (size / (1024 * 1024 * 1024)).toFixed(2) + ' GB';
    };

    const handleImageError = event => {
      const target = event.target;
      target.style.display = 'none';
      target.parentElement.classList.add('image-error');
    };

    const handleDelete = upload => {
      if (!upload.realId || !upload.canDelete) return;

      // Use the global delete confirmation store
      root.$store.dispatch('deleteConfirmation/showDeleteConfirmation', {
        item: upload,
        type: 'upload',
        contentCount: 1,
        contentType: upload.contentType === 'video' ? 'video' : 'image',
        onConfirm: async uploadToDelete => {
          try {
            // Call the store's delete action
            const result = await root.$store.dispatch('uploadProgress/deleteUpload', {
              uploadId: uploadToDelete.id,
              realId: uploadToDelete.realId,
              contentType: uploadToDelete.contentType
            });

            if (result.success) {
              root.$q.notify({
                type: 'positive',
                message: `"${uploadToDelete.fileName}" deleted successfully`,
                position: 'top',
                timeout: 2000
              });
            } else {
              throw new Error(result.error);
            }
          } catch (error) {
            console.error('Error deleting upload:', error);
            root.$q.notify({
              type: 'negative',
              message: `Failed to delete "${uploadToDelete.fileName}": ${error.message}`,
              position: 'top',
              timeout: 3000
            });
            throw error; // Re-throw to be handled by the store
          }
        }
      });
    };

    const handleRetry = () => {
      // Placeholder for retry functionality - will be implemented later
      root.$q.notify({
        type: 'info',
        message: 'Retry functionality will be available soon',
        position: 'top',
        timeout: 2000
      });
    };

    const getHeaderTitle = () => {
      if (uploads.value.length === 0) {
        return 'Upload Progress';
      }

      const hasActiveUploadsValue = uploads.value.some(upload => upload.progress < 100 && upload.status !== 'error');
      const hasErrors = uploads.value.some(upload => upload.status === 'error');

      if (hasActiveUploadsValue) {
        return 'Upload in progress...';
      } else if (hasErrors && uploads.value.every(upload => upload.status === 'error' || upload.progress >= 100)) {
        return 'Upload completed with errors';
      } else {
        return 'Upload completed';
      }
    };

    const getHeaderSubtitle = () => {
      if (uploads.value.length === 0) {
        return '';
      }

      const fileText = uploads.value.length === 1 ? 'file' : 'files';
      return `${uploads.value.length} ${fileText} into the storage`;
    };

    // Watch for route changes to reopen dialog if there are active uploads
    watch(
      () => root.$route.path,
      (newPath, oldPath) => {
        // Only reopen if route actually changed and there are active uploads
        if (newPath !== oldPath && hasActiveUploads.value && !isVisible.value) {
          // Automatically reopen the dialog when navigating with active uploads
          root.$store.dispatch('uploadProgress/showDialog');
        }
      },
      { immediate: false }
    );

    return {
      // Configuration
      CONFIG,

      // Reactive data
      isMinimized,
      animatedProgress,
      displayProgress,
      activeAnimations,
      animationTargets,
      displayIntervals,

      // Computed
      isVisible,
      uploads,
      activeUploads,
      completedUploads,
      totalActiveUploads,
      hasActiveUploads,
      uploadsNeedingCheck,

      // Query
      processingStatusQuery,

      // Methods
      toggleMinimize,
      handleClose,
      hideDialog,
      clearCompleted,
      clearAll,
      animateProgressTo,
      startInternalAnimation,
      startDisplayCounting,
      cleanupDisplayInterval,
      getAdaptiveStepSize,
      cleanupAnimation,
      cleanupAnimationTimeout,
      getAnimatedProgress,
      getProgressColor,
      shouldShowProgress,
      getContentTypeIcon,
      truncateFileName,
      formatFileSize,
      handleImageError,
      handleDelete,
      handleRetry,
      getHeaderTitle,
      getHeaderSubtitle
    };
  },

  // Optimized watchers with better performance and maintainability
  watch: {
    uploads: {
      handler(newUploads, oldUploads) {
        // Process each upload efficiently
        newUploads.forEach(upload => {
          this.processUploadProgress(upload);
        });

        // Clean up removed uploads
        this.cleanupRemovedUploads(newUploads, oldUploads);
      },
      deep: true,
      immediate: false
    }
  },

  methods: {
    // Separated upload processing logic for better maintainability
    processUploadProgress(upload) {
      const currentAnimated = this.animatedProgress[upload.id];
      const currentDisplay = this.displayProgress[upload.id];
      const realProgress = upload.progress || 0;

      // Handle internal animation tracking (fast, responsive)
      this.handleInternalAnimation(upload.id, realProgress, currentAnimated);

      // Handle display counting (slow, visual effect)
      this.handleDisplayAnimation(upload.id, realProgress, currentDisplay);
    },

    // Internal animation handling
    handleInternalAnimation(uploadId, realProgress, currentAnimated) {
      const shouldStartNew = !(uploadId in this.animatedProgress);
      const shouldUpdate = realProgress > (currentAnimated || 0) + this.CONFIG.INTERNAL_PROGRESS_THRESHOLD;

      if ((shouldStartNew || shouldUpdate) && realProgress > 0) {
        this.animateProgressTo(uploadId, realProgress);
      }
    },

    // Display animation handling
    handleDisplayAnimation(uploadId, realProgress, currentDisplay) {
      const shouldStartNew = !(uploadId in this.displayProgress);
      const shouldUpdate = realProgress > (currentDisplay || 0) + this.CONFIG.DISPLAY_PROGRESS_THRESHOLD;

      if ((shouldStartNew || shouldUpdate) && realProgress > 0) {
        this.startDisplayCounting(uploadId, realProgress);
      }
    },

    // Centralized cleanup for removed uploads
    cleanupRemovedUploads(newUploads, oldUploads) {
      if (!oldUploads) return;

      const newUploadIds = new Set(newUploads.map(u => u.id));
      const removedUploadIds = Object.keys(this.animatedProgress).filter(id => !newUploadIds.has(id));

      removedUploadIds.forEach(uploadId => {
        this.cleanupUploadData(uploadId);
      });
    },

    // Complete cleanup for a single upload
    cleanupUploadData(uploadId) {
      // Clear internal animation
      if (this.activeAnimations[uploadId]) {
        clearInterval(this.activeAnimations[uploadId]);
        this.$delete(this.activeAnimations, uploadId);
      }

      // Clear animation targets
      if (this.animationTargets[uploadId]) {
        this.$delete(this.animationTargets, uploadId);
      }

      // Clear display interval
      if (this.displayIntervals[uploadId]) {
        clearInterval(this.displayIntervals[uploadId]);
        this.$delete(this.displayIntervals, uploadId);
      }

      // Clear progress data
      this.$delete(this.animatedProgress, uploadId);
      this.$delete(this.displayProgress, uploadId);
    },

    // Optimized cleanup methods
    cleanupAllAnimations() {
      Object.values(this.activeAnimations).forEach(interval => {
        if (interval) clearInterval(interval);
      });
      this.activeAnimations = {};
    },

    cleanupAllDisplayIntervals() {
      Object.values(this.displayIntervals).forEach(interval => {
        if (interval) clearInterval(interval);
      });
      this.displayIntervals = {};
    }
  },

  beforeDestroy() {
    // Comprehensive cleanup of all intervals and timers
    this.cleanupAllAnimations();
    this.cleanupAllDisplayIntervals();

    // Clear all data structures
    this.animationTargets = {};
  }
};
</script>

<style lang="scss" scoped>
.global-upload-progress {
  position: fixed;
  bottom: 20px;
  right: 20px;
  background: var(--secondary-bg-color);
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(0, 0, 0, 0.08);
  width: 360px;
  z-index: 10001;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &.minimized {
    .upload-content {
      display: none;
    }
  }
}

.upload-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  background: var(--secondary-bg-color);
  border-radius: 12px 12px 0 0;
  min-height: 52px;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
}

.upload-icon-wrapper {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: var(--video-chip-bg);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.upload-icon {
  color: var(--video-chip-text);
  flex-shrink: 0;
}

.header-info {
  display: flex;
  flex-direction: column;
  justify-content: center;
  flex: 1;
  min-width: 0;
}

.upload-title {
  @include mona-sans-font($size: 16px, $bolded: true, $color: var(--main-text-color));
  line-height: 1.2;
  margin: 0;
  text-align: left;
}

.upload-subtitle {
  @include inter-font($size: 13px, $color: var(--secondary-text-color));
  line-height: 1.2;
  margin-top: 2px;
  text-align: left;
}

.header-actions {
  display: flex;
  gap: 0;
  align-items: center;
}

.header-btn {
  color: var(--secondary-text-color);
  width: 24px;
  height: 24px;
  border-radius: 4px;

  &:hover {
    color: var(--main-text-color);
    background: rgba(0, 0, 0, 0.05);
  }

  .q-icon {
    font-size: 16px;
  }
}

.upload-content {
  max-height: 280px;
  overflow-y: auto;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
}

.upload-list {
  padding: 0;
}

.upload-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.04);
  transition: background-color 0.2s ease;

  &:hover {
    background: var(--light-grey-hr);
  }

  &:last-child {
    border-bottom: none;
  }
}

.upload-thumbnail {
  position: relative;
  width: 40px;
  height: 40px;
  border-radius: 6px;
  overflow: hidden;
  background: var(--light-grey-hr);
  flex-shrink: 0;
  border: 1px solid rgba(0, 0, 0, 0.08);
}

.thumbnail-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.thumbnail-fallback {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background: var(--light-grey-hr);
}

.thumbnail-fallback.image-error {
  background: var(--light-grey-hr);
}

.file-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.file-main-info {
  display: flex;
  align-items: center;
  gap: 8px;
}

.file-type-icon {
  flex-shrink: 0;
}

.file-name {
  @include mona-sans-font($size: 13px, $weight: 500, $color: var(--main-text-color));
  line-height: 1.3;
  word-break: break-word;
}

.upload-actions {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  min-width: 60px;
  justify-content: flex-end;
}

.action-btn {
  width: 28px;
  height: 28px;
  border-radius: 4px;
  transition: all 0.2s ease;

  &.delete-btn {
    &:hover {
      background: rgba(var(--negative), 0.08);
    }
  }

  &.retry-btn {
    &:hover {
      background: rgba(var(--warning), 0.08);
    }
  }
}

.progress-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  gap: 6px;
  min-width: 60px;
}

.progress-text {
  @include inter-font($size: 11px, $weight: 500, $color: var(--main-text-color));
  line-height: 1;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
  transition: all 0.2s ease;
  font-weight: 600;

  // Subtle animation during progress updates
  .progress-indicator:has(.progress-active) & {
    animation: textPulse 1.5s ease-in-out infinite;
    color: var(--primary);
    font-weight: 700;
  }

  // Fallback for browsers without :has() support
  .progress-active + & {
    animation: textPulse 1.5s ease-in-out infinite;
    color: var(--primary);
    font-weight: 700;
  }
}

.upload-progress {
  transition: all 0.3s ease;

  &.progress-active {
    animation: progressPulse 2s ease-in-out infinite;
  }
}

.success-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
}

.warning-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
}

.error-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
}

.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-up-enter-from,
.slide-up-leave-to {
  opacity: 0;
  transform: translateY(20px) scale(0.95);
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@keyframes progressPulse {
  0%,
  100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.05);
    opacity: 0.9;
  }
}

@keyframes textPulse {
  0%,
  100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.05);
    opacity: 0.9;
  }
}

@media (max-width: 480px) {
  .global-upload-progress {
    right: 16px;
    bottom: 16px;
    width: calc(100vw - 32px);
    max-width: 360px;
  }

  .upload-item {
    padding: 10px 16px;
    gap: 10px;
  }

  .upload-thumbnail {
    width: 36px;
    height: 36px;
  }

  .file-name {
    font-size: 12px;
  }

  .header-left {
    gap: 8px;
  }

  .progress-indicator {
    min-width: 55px;
    gap: 4px;
  }

  .progress-text {
    font-size: 10px;
  }

  .upload-actions {
    min-width: 55px;
  }
}

:deep(.error-tooltip .q-tooltip),
:deep(.success-tooltip .q-tooltip),
:deep(.warning-tooltip .q-tooltip),
.error-tooltip .q-tooltip,
.success-tooltip .q-tooltip,
.warning-tooltip .q-tooltip {
  background: var(--secondary-bg-color) !important;
  padding: 0 !important;
  position: fixed !important;
  white-space: nowrap !important;
}

.tooltip-content {
  padding: 8px 12px;
}

.tooltip-title {
  @include mona-sans-font($size: 12px, $bolded: true, $color: #ffffff);
  margin-bottom: 2px;
  line-height: 1.2;
}

.tooltip-message {
  @include inter-font($size: 11px, $color: #e5e5e5);
  line-height: 1.3;
  word-break: break-word;
}
</style>

<style lang="scss">
.q-tooltip {
  z-index: 99999 !important;
}

.global-upload-progress {
  .q-tooltip {
    z-index: 99999 !important;
  }
}
</style>
